import { useState } from 'react';
import {
  Stack, Chip, Alert, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, MenuItem,
  Box, IconButton, Tooltip, Link, Divider,
} from '@mui/material';
import { Add, Visibility, Delete, EmojiEvents, LockOpen, Lock, Download } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { extractApiError } from '../../api/error';
import { epcRfqApi, type RfqListItem, type RfqDetail, type AddQuoteInput } from '../../api/epcRfq.api';
import { vendorsApi } from '../../api/vendors.api';

const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

/**
 * RFQ: generate a persisted request that consolidates procurement-accepted materials, capture a
 * quotation per vendor, and compare rates line-item-wise — the lowest per line (and lowest total) is
 * highlighted automatically.
 */
export function EpcRfqPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [openId, setOpenId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['epc-rfq', page, limit], queryFn: () => epcRfqApi.list({ page, limit }) });
  const { data: preview } = useQuery({ queryKey: ['epc-rfq-merged'], queryFn: () => epcRfqApi.mergedItems() });

  const generate = useMutation({
    mutationFn: () => epcRfqApi.generate({}),
    onSuccess: (rfq) => { qc.invalidateQueries({ queryKey: ['epc-rfq'] }); setOpenId(rfq.id); },
    onError: (e) => setErr(extractApiError(e)),
  });

  const columns: Column<RfqListItem>[] = [
    { key: 'rfqCode', label: 'RFQ #' },
    { key: 'title', label: 'Title', render: (r) => r.title ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={r.status === 'open' ? 'success' : 'default'} label={r.status} /> },
    { key: 'itemCount', label: 'Materials', align: 'right', render: (r) => r.itemCount },
    { key: 'quoteCount', label: 'Quotations', align: 'right', render: (r) => (
      r.quoteCount > 0
        ? <Link component="button" type="button" underline="hover" onClick={() => setOpenId(r.id)}>{r.quoteCount}</Link>
        : <Typography variant="body2" color="text.secondary" component="span">0</Typography>
    ) },
    { key: 'createdAt', label: 'Created', render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : '—') },
  ];

  const pendingCount = preview?.items.length ?? 0;

  return (
    <>
      <PageHeader title="Request for Quotation (RFQ)" subtitle="Generate an RFQ from procurement-accepted materials, upload each vendor's quotation, and compare rates line-item-wise." />
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} disabled={pendingCount === 0 || generate.isPending} onClick={() => generate.mutate()}>
          Generate RFQ
        </Button>
        <Typography variant="body2" color="text.secondary">
          {pendingCount > 0
            ? `${pendingCount} material(s) from ${preview?.requisitionCount ?? 0} procurement-accepted requisition(s) ready to consolidate.`
            : 'No procurement-accepted materials pending. Accept requisitions in Procurement Review first.'}
        </Typography>
      </Stack>

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading} rowKey={(r) => r.id}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }}
        actions={(r) => <Button size="small" startIcon={<Visibility />} onClick={() => setOpenId(r.id)}>View</Button>}
        emptyTitle="No RFQs yet" emptyDescription="Generate an RFQ to start collecting vendor quotations."
      />

      {openId != null && <RfqDetailDialog id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function RfqDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: rfq, isLoading } = useQuery({ queryKey: ['epc-rfq', 'detail', id], queryFn: () => epcRfqApi.getById(id) });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['epc-rfq', 'detail', id] }); qc.invalidateQueries({ queryKey: ['epc-rfq'] }); };
  const removeQuote = useMutation({ mutationFn: (quoteId: number) => epcRfqApi.removeQuote(id, quoteId), onSuccess: invalidate, onError: (e) => setErr(extractApiError(e)) });
  const setStatus = useMutation({ mutationFn: (status: 'open' | 'closed') => epcRfqApi.setStatus(id, status), onSuccess: invalidate, onError: (e) => setErr(extractApiError(e)) });

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {rfq ? `${rfq.rfqCode}` : 'RFQ'}{' '}
        {rfq && <Chip size="small" sx={{ ml: 1 }} color={rfq.status === 'open' ? 'success' : 'default'} label={rfq.status} />}
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {isLoading && <Typography>Loading…</Typography>}
        {rfq && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Rates are compared per material: the cheapest is highlighted green <b>only when two or more vendors have quoted that same material</b> (a single quote has nothing to compare against). The vendor cheapest on the most compared materials is flagged with a trophy.
            </Alert>
            {rfq.quotes.length === 0 && <Alert severity="warning" sx={{ mb: 2 }}>No quotations uploaded yet. Click "Add Quotation" to enter a vendor's rates.</Alert>}
            {rfq.quotes.length > 0 && <QuotationsPanel rfq={rfq} onRemoveQuote={(qid) => removeQuote.mutate(qid)} removable={rfq.status === 'open'} />}
            <ComparisonMatrix rfq={rfq} onRemoveQuote={(qid) => removeQuote.mutate(qid)} removable={rfq.status === 'open'} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {rfq && rfq.status === 'open' && <Button startIcon={<Add />} variant="contained" onClick={() => setAddOpen(true)}>Add Quotation</Button>}
        {rfq && rfq.status === 'open' && <Button startIcon={<Lock />} color="inherit" onClick={() => setStatus.mutate('closed')}>Close RFQ</Button>}
        {rfq && rfq.status === 'closed' && <Button startIcon={<LockOpen />} color="inherit" onClick={() => setStatus.mutate('open')}>Reopen</Button>}
        <Box flex={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {addOpen && rfq && <AddQuoteDialog rfq={rfq} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); invalidate(); }} />}
    </Dialog>
  );
}

/** All quotations captured for the RFQ: vendor, quoted-line count, total, remarks + a download link. */
function QuotationsPanel({ rfq, onRemoveQuote, removable }: { rfq: RfqDetail; onRemoveQuote: (quoteId: number) => void; removable: boolean }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Quotations received ({rfq.quotes.length})</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Quotation No.</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Received</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lines quoted</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Document</TableCell>
              {removable && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rfq.quotes.map((q) => (
              <TableRow key={q.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {q.id === rfq.winnerQuoteId && <Tooltip title="Cheapest on the most compared materials"><EmojiEvents fontSize="small" color="success" /></Tooltip>}
                    <span>{q.vendorName ?? 'Vendor'}</span>
                    {q.wins > 0 && <Chip size="small" color="success" variant="outlined" label={`${q.wins} lowest`} />}
                  </Stack>
                </TableCell>
                <TableCell>{q.quotationNumber || '—'}</TableCell>
                <TableCell>{q.quotationDate ? new Date(q.quotationDate).toLocaleDateString('en-IN') : (q.receivedAt ? new Date(q.receivedAt).toLocaleDateString('en-IN') : '—')}</TableCell>
                <TableCell align="right">{q.quotedLines}</TableCell>
                <TableCell align="right">{inr(q.total)}</TableCell>
                <TableCell>{q.remarks || '—'}</TableCell>
                <TableCell>
                  {q.quotationUrl
                    ? <Link href={q.quotationUrl} target="_blank" rel="noreferrer" download sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><Download fontSize="small" /> Download</Link>
                    : <Typography variant="caption" color="text.secondary">Not attached</Typography>}
                </TableCell>
                {removable && (
                  <TableCell align="right">
                    <Tooltip title="Remove quotation"><IconButton size="small" onClick={() => onRemoveQuote(q.id)}><Delete fontSize="inherit" /></IconButton></Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider sx={{ my: 2 }} />
    </Box>
  );
}

function ComparisonMatrix({ rfq, onRemoveQuote, removable }: { rfq: RfqDetail; onRemoveQuote: (quoteId: number) => void; removable: boolean }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Material</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
            {rfq.quotes.map((q) => (
              <TableCell key={q.id} align="right" sx={{ fontWeight: 700, minWidth: 130, bgcolor: q.id === rfq.winnerQuoteId ? 'success.light' : undefined }}>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {q.id === rfq.winnerQuoteId && <Tooltip title="Cheapest on the most compared materials"><EmojiEvents fontSize="small" color="success" /></Tooltip>}
                  <span>{q.vendorName ?? 'Vendor'}</span>
                  {removable && (
                    <Tooltip title="Remove quotation"><IconButton size="small" onClick={() => onRemoveQuote(q.id)}><Delete fontSize="inherit" /></IconButton></Tooltip>
                  )}
                </Stack>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rfq.items.map((it) => (
            <TableRow key={it.id} hover>
              <TableCell>{it.description}</TableCell>
              <TableCell align="right">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</TableCell>
              {rfq.quotes.map((q) => {
                const cell = it.rates.find((r) => r.quoteId === q.id);
                const rate = cell?.rate ?? null;
                const isLow = it.lowestQuoteId === q.id && rate != null;
                return (
                  <TableCell key={q.id} align="right" sx={{ bgcolor: isLow ? 'success.light' : undefined, fontWeight: isLow ? 700 : 400 }}>
                    {rate == null ? '—' : inr(rate)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Total on quoted lines (rate × qty)</TableCell>
            <TableCell />
            {rfq.quotes.map((q) => (
              <TableCell key={q.id} align="right" sx={{ fontWeight: 700 }}>
                {inr(q.total)}
                <br /><Typography variant="caption" color="text.secondary">{q.quotedLines} line(s)</Typography>
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Materials won (cheapest)</TableCell>
            <TableCell />
            {rfq.quotes.map((q) => (
              <TableCell key={q.id} align="right" sx={{ fontWeight: 700, bgcolor: q.id === rfq.winnerQuoteId ? 'success.light' : undefined }}>
                {q.wins}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AddQuoteDialog({ rfq, onClose, onSaved }: { rfq: RfqDetail; onClose: () => void; onSaved: () => void }) {
  const [vendorId, setVendorId] = useState<string>('');
  const [vendorName, setVendorName] = useState('');
  const [vendorGstin, setVendorGstin] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [vendorMobile, setVendorMobile] = useState('');
  const [quotationNumber, setQuotationNumber] = useState('');
  const [quotationDate, setQuotationDate] = useState('');
  const [quotationUrl, setQuotationUrl] = useState('');
  const [remarks, setRemarks] = useState('');
  const [rates, setRates] = useState<Record<number, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [extractMsg, setExtractMsg] = useState<{ severity: 'success' | 'info' | 'warning'; text: string } | null>(null);

  const { data: vendors } = useQuery({ queryKey: ['vendors', 'all'], queryFn: () => vendorsApi.list({ limit: 500 }) });

  // Read the uploaded quotation and pre-fill the rate for each matched line item.
  const extract = useMutation({
    mutationFn: (url: string) => epcRfqApi.extractQuote(rfq.id, url),
    onSuccess: (res) => {
      if (res.lines.length) {
        setRates((prev) => {
          const next = { ...prev };
          for (const l of res.lines) next[l.rfqItemId] = String(l.rate);
          return next;
        });
      }
      if (!vendorId && res.vendorName) setVendorName((v) => v || res.vendorName || '');
      if (!vendorId && res.vendorGstin) setVendorGstin((v) => v || res.vendorGstin || '');
      if (!vendorId && res.vendorEmail) setVendorEmail((v) => v || res.vendorEmail || '');
      if (!vendorId && res.vendorMobile) setVendorMobile((v) => v || res.vendorMobile || '');
      if (res.quotationNumber) setQuotationNumber((v) => v || res.quotationNumber || '');
      if (res.quotationDate) setQuotationDate((v) => v || res.quotationDate || '');
      if (!res.configured) setExtractMsg({ severity: 'info', text: res.message ?? 'Auto-extract not configured — enter the rates manually.' });
      else if (res.matchedCount > 0) setExtractMsg({ severity: 'success', text: `Auto-filled ${res.matchedCount} of ${res.itemCount} rate(s) from the quotation. Review before saving.` });
      else setExtractMsg({ severity: 'warning', text: res.message ?? 'Could not match any rates from the quotation — enter them manually.' });
    },
    onError: (e) => setExtractMsg({ severity: 'warning', text: extractApiError(e) }),
  });

  const onUpload = (url: string | null) => {
    setQuotationUrl(url ?? '');
    setExtractMsg(null);
    if (url) extract.mutate(url);
  };

  const save = useMutation({
    mutationFn: () => {
      const input: AddQuoteInput = {
        vendorId: vendorId ? Number(vendorId) : null,
        vendorName: vendorId ? null : (vendorName.trim() || null),
        vendorGstin: vendorId ? null : (vendorGstin.trim() || null),
        vendorEmail: vendorId ? null : (vendorEmail.trim() || null),
        vendorMobile: vendorId ? null : (vendorMobile.trim() || null),
        quotationNumber: quotationNumber.trim() || null,
        quotationDate: quotationDate || null,
        quotationUrl: quotationUrl || null,
        remarks: remarks || null,
        lines: rfq.items
          .map((it) => ({ rfqItemId: it.id, rate: Number(rates[it.id] ?? 0) }))
          .filter((l) => l.rate > 0),
      };
      return epcRfqApi.addQuote(rfq.id, input);
    },
    onSuccess: onSaved,
    onError: (e) => setErr(extractApiError(e)),
  });

  // The received date cannot pre-date the RFQ; block it in the UI too (server also validates).
  const dateTooEarly = !!(quotationDate && rfq.rfqDate && quotationDate < rfq.rfqDate);
  const canSave = (!!vendorId || vendorName.trim().length > 0) && !dateTooEarly;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Quotation — {rfq.rfqCode}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Vendor (onboarded)" value={vendorId} onChange={(e) => setVendorId(e.target.value)} fullWidth>
            <MenuItem value="">— Not onboarded / enter manually —</MenuItem>
            {(vendors?.data ?? []).map((v) => <MenuItem key={v.id} value={String(v.id)}>{v.vendorName}</MenuItem>)}
          </TextField>
          {!vendorId && (
            <>
              <TextField label="Vendor name" value={vendorName} onChange={(e) => setVendorName(e.target.value)} fullWidth required
                helperText="A new vendor is auto-onboarded (deduped by name) and added to Vendor Lifecycle." />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="GSTIN (optional)" value={vendorGstin} onChange={(e) => setVendorGstin(e.target.value)} fullWidth />
                <TextField label="Email (optional)" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)} fullWidth />
                <TextField label="Mobile (optional)" value={vendorMobile} onChange={(e) => setVendorMobile(e.target.value)} fullWidth />
              </Stack>
            </>
          )}
          <FileUploadField label="Quotation document (rates auto-extracted on upload)" accept=".pdf,image/*,.xls,.xlsx" value={quotationUrl} onChange={onUpload} />
          {extract.isPending && <Alert severity="info">Reading the quotation and matching rates…</Alert>}
          {extractMsg && !extract.isPending && <Alert severity={extractMsg.severity} onClose={() => setExtractMsg(null)}>{extractMsg.text}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Quotation number" value={quotationNumber} onChange={(e) => setQuotationNumber(e.target.value)} fullWidth
              helperText="Auto-filled from the document. Used with the date to block duplicate uploads." />
            <TextField label="Quotation (received) date" type="date" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} fullWidth
              InputLabelProps={{ shrink: true }} inputProps={{ min: rfq.rfqDate ?? undefined }}
              error={dateTooEarly} helperText={dateTooEarly ? `Cannot be earlier than the RFQ date (${rfq.rfqDate})` : 'Auto-filled from the document.'} />
          </Stack>
          <TextField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

          <Typography variant="subtitle2">Quoted rate per material</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 160 }}>Rate (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfq.items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.description}</TableCell>
                    <TableCell align="right">{it.quantity}{it.unit ? ` ${it.unit}` : ''}</TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={rates[it.id] ?? ''} onChange={(e) => setRates((p) => ({ ...p, [it.id]: e.target.value }))}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={{ width: 140 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>Save Quotation</Button>
      </DialogActions>
    </Dialog>
  );
}
